import { useEffect, useState, type FormEvent } from 'react';
import LanguagePicker from '../i18n/LanguagePicker';
import { Link, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { useT } from '../i18n/i18n';
import { incompleteEmail } from '../auth/emailCheck';
import { api } from '../api/client';
import { SCHOOL_KEEP_KEY } from './SsoCallbackPage';

export default function LoginPage() {
  const { login, loginWithCode, emailLoginCode, register, token, ready } = useAuth();
  const t = useT();
  const [mode, setMode] = useState<'login' | 'register'>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [challenge, setChallenge] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [emailed, setEmailed] = useState(false);
  const [keepOnDevice, setKeepOnDevice] = useState(false);
  const loc = useLocation() as { state?: { from?: string } };
  const [school, setSchool] = useState<string | null>(null);

  useEffect(() => {
    setSchool(null);
    const value = email.trim();
    if (mode !== 'login' || !/^[^@s]+@[^@s]+.[^@s]{2,}$/.test(value)) return;
    let live = true;
    const timer = setTimeout(() => {
      api<{ school: boolean; name: string | null }>('/auth/discover?email=' + encodeURIComponent(value))
        .then((d) => { if (live && d.school) setSchool(d.name); })
        .catch(() => {});
    }, 350);
    return () => { live = false; clearTimeout(timer); };
  }, [email, mode]);

  async function continueToSchool() {
    setError(null);
    setBusy(true);
    try {
      const res = await api<{ url: string }>('/auth/sso/start', { method: 'POST', body: { email: email.trim() } });
      try { sessionStorage.setItem(SCHOOL_KEEP_KEY, keepOnDevice ? '1' : '0'); } catch { /* storage off */ }
      window.location.assign(res.url);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('Sign-in failed'));
      setBusy(false);
    }
  }

  if (ready && token) {
    return <Navigate to={loc.state?.from ?? '/'} replace />;
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (school) { void continueToSchool(); return; }
    setError(null);
    if (incompleteEmail(email)) { setError(t('This email address looks incomplete. Check the end, for example .com or .co.uk.')); return; }
    setBusy(true);
    try {
      if (mode === 'login') {
        const next = await login(email, password, keepOnDevice);
        if (next) { setChallenge(next.challenge); setPassword(''); }
      } else {
        const res = await register(email, password, displayName);
        setSent(t(res.next));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('Sign-in failed'));
    } finally {
      setBusy(false);
    }
  }

  async function submitCode(e: FormEvent) {
    e.preventDefault();
    if (!challenge) return;
    setError(null);
    setBusy(true);
    try {
      await loginWithCode(challenge, code, keepOnDevice);
    } catch (err) {
      const msg = err instanceof Error ? err.message : t('Sign-in failed');
      const status = (err as { status?: number } | null)?.status;
      if (status === 401 || status === 429) { setChallenge(null); setCode(''); setEmailed(false); }
      setError(msg);
    } finally {
      setBusy(false);
    }
  }

  async function sendEmail() {
    if (!challenge) return;
    setError(null);
    setBusy(true);
    try {
      await emailLoginCode(challenge);
      setEmailed(true);
      setCode('');
    } catch (err) {
      setError(err instanceof Error ? err.message : t('That could not be sent just now. Please try again.'));
    } finally {
      setBusy(false);
    }
  }

  if (challenge) {
    return (
      <section aria-labelledby="code-heading" style={{ maxWidth: 460, margin: '0 auto' }}>
        <div className="card">
          <h2 id="code-heading">{t('Enter your sign-in code')}</h2>
          <p>
            {emailed
              ? t('We have emailed a 6-digit code to {email}. It works for 10 minutes.', { email })
              : t('Open the authenticator app on your phone and type the 6-digit code for EasyLearning. You can also use one of your backup codes.')}
          </p>
          <form onSubmit={submitCode} className="spaced" noValidate>
            {error && <div className="error" role="alert">{error}</div>}
            <div className="field">
              <label htmlFor="signin-code">{t('Code')}</label>
              <input
                id="signin-code"
                inputMode={emailed ? 'numeric' : 'text'}
                autoComplete="one-time-code"
                autoFocus
                maxLength={12}
                value={code}
                onChange={(e) => setCode(e.target.value)}
              />
            </div>
            <div className="toolbar">
              <button className="btn" type="submit" disabled={busy || code.trim().length < 6}>
                {busy ? t('Working…') : t('Sign in')}
              </button>
              <button type="button" className="btn ghost" onClick={() => { setChallenge(null); setCode(''); setEmailed(false); setError(null); }}>
                {t('Back to sign in')}
              </button>
            </div>
          </form>
          <p className="field-hint">
            {emailed ? t('No email? Check your spam folder, or send another code.') : t('No phone or backup code to hand?')}
          </p>
          <div className="toolbar">
            <button type="button" className="btn ghost" onClick={() => void sendEmail()} disabled={busy}>
              {emailed ? t('Send another code') : t('Email me a code instead')}
            </button>
          </div>
        </div>
      </section>
    );
  }

  /* THE STEP THAT USED NOT TO EXIST. Creating the account no longer signs
     anybody in: the address has to be confirmed first, and saying so plainly
     here is the difference between a supervisor waiting for an email and a
     supervisor typing their new password over and over. */
  if (sent) {
    return (
      <section aria-labelledby="sent-heading" style={{ maxWidth: 460, margin: '0 auto' }}>
        <div className="card">
          <h2 id="sent-heading">{t('Check your email')}</h2>
          <p>{sent}</p>
          <p className="field-hint">
            {t('Sent to')} <strong>{email}</strong>. {t('If it does not arrive within a few minutes, check your spam folder.')}
          </p>
          <div className="toolbar">
            <button
              className="btn"
              type="button"
              onClick={() => { setSent(null); setMode('login'); setPassword(''); }}
            >
              {t('Back to sign in')}
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section aria-labelledby="auth-heading" style={{ maxWidth: 460, margin: '0 auto' }}>
      <div className="card">
        <h2 id="auth-heading">{mode === 'login' ? t('Sign in') : t('Create account')}</h2>
        <p className="muted" style={{ marginTop: 0 }}>
          {t('A monthly subscription is required to access programs. New accounts begin with a 7-day trial.')}
        </p>
        <LanguagePicker id="login-language" />
        <form onSubmit={submit} className="spaced" noValidate>
          {error && <div className="error" role="alert">{error}</div>}
          {mode === 'register' && (
            <div className="field">
              <label htmlFor="dn">{t('Name shown to you')}</label>
              <input
                id="dn"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                maxLength={80}
              />
            </div>
          )}
          <div className="field">
            <label htmlFor="email">{t('Email')}</label>
            <input
              id="email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          {school ? (
            <p className="field-hint" role="status">{t('This address belongs to {school}. You sign in with your school account.', { school })}</p>
          ) : (
          <div className="field">
            <label htmlFor="pw">{t('Password')}</label>
            <input
              id="pw"
              type="password"
              autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
              minLength={mode === 'register' ? 12 : 1}
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            {mode === 'register' && (
              <span className="field-hint">
                {t('At least 12 characters, and not the same as your email address.')}
              </span>
            )}
            {mode === 'login' && (
              <Link className="field-hint" to="/forgot-password" state={{ email }}>{t('Forgotten your password?')}</Link>
            )}
          </div>
          )}
          {mode === 'login' && (
            <div className="field">
              <label className="check">
                <input type="checkbox" checked={keepOnDevice} onChange={(e) => setKeepOnDevice(e.target.checked)} />
                {t('Keep me signed in on this device')}
              </label>
              <span className="field-hint">{t('Only on your own phone or computer. On a shared device, leave this unticked so the next person cannot open your account.')}</span>
            </div>
          )}
          {mode === 'register' && (
            <div className="field">
              <label className="check">
                <input
                  type="checkbox"
                  checked={agreed}
                  onChange={(e) => setAgreed(e.target.checked)}
                />
                {t('I have read and agree to the terms')}
              </label>
              <span className="field-hint">
                {t('What this is, what it is not, and what it keeps about a learner:')}{' '}
                <Link to="/terms" target="_blank" rel="noopener">{t('read the terms')}</Link>.
                {t('How we look after your information:')}{' '}
                <Link to="/privacy" target="_blank" rel="noopener">{t('read the privacy notice')}</Link>.
              </span>
            </div>
          )}
          <div className="toolbar">
            <button className="btn" type="submit" disabled={busy || (mode === 'register' && !agreed)}>
              {busy ? t('Working…') : school ? t('Continue to school sign-in') : mode === 'login' ? t('Sign in') : t('Create account')}
            </button>
            <button
              type="button"
              className="btn ghost"
              onClick={() => { setMode(mode === 'login' ? 'register' : 'login'); setError(null); }}
            >
              {mode === 'login' ? t('Need an account?') : t('Have an account? Sign in')}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}
